import { createFormData } from "@app/helpers/utils";
import { sansLesClesPriveesDeCopie } from "../Duplication/cles-privees";
import { ScheduleAvailabilityTypeEnum, IPromotionCondition as IOriginalPromotionCondition, IPromotion, ISchedule, IShop, IPromotionAction as IOriginalPromotionAction, PromotionConditionType, IProductVariation, ITag, ICustomer, IPack, PromotionActionType, convertToCents, PromotionConditionOperator, IPromotionConditionBirthdayWindow, IPromotionConditionCustomers, IPromotionConditionMinOrderTotal, ReferenceId, IPromotionActionItemsDiscountFixed, IPromotionActionItemsDiscountPercentage, IPromotionActionOrderDiscountFixed, IPromotionActionOrderDiscountPercentage, isPromotionCode, convertToEuros, PromotionScope } from "@app/common";

type IPromotionConditionPacks = {
  type: PromotionConditionType.PACKS;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
    value: IPack[];
    quantity: number;
  }
}

type IPromotionConditionProductVariations = {
  type: PromotionConditionType.PRODUCT_VARIATIONS;
  referenceId: ReferenceId;
  details: {
    operator: PromotionConditionOperator.IN | PromotionConditionOperator.NOT_IN;
    value: IProductVariation[];
    quantity: number;
  }
}

type IPromotionActionAddProductVariation = {
  type: PromotionActionType.ADD_PRODUCT_VARIATION;
  referenceId: ReferenceId;
  details: {
    value: IProductVariation;
  }
}

type IPromotionActionAddPack = {
  type: PromotionActionType.ADD_PACK;
  referenceId: ReferenceId;
  details: {
    value: IPack;
  }
}

type IPromotionAction = IPromotionActionAddProductVariation
  | IPromotionActionAddPack
  | IPromotionActionOrderDiscountFixed
  | IPromotionActionOrderDiscountPercentage
  | IPromotionActionItemsDiscountFixed
  | IPromotionActionItemsDiscountPercentage;


type IPromotionCondition = IPromotionConditionMinOrderTotal
  | IPromotionConditionBirthdayWindow
  | IPromotionConditionPacks
  | IPromotionConditionProductVariations
  | IPromotionConditionCustomers;

export interface PromotionFormValues extends Partial<Omit<IPromotion, 'conditions' | 'actions'>> {
  image: any;
  shops: Pick<IShop, '_id' | 'internalName' | 'publicName'>[];
  isPromotionCode: boolean;
  availabilityType: ScheduleAvailabilityTypeEnum;
  conditions: IPromotionCondition[];
  actions: IPromotionAction[];
}

const transformCondition = (condition: IPromotionCondition): IOriginalPromotionCondition => {
  if (condition.type === PromotionConditionType.PRODUCT_VARIATIONS) {
    const productVariationsIds = (condition.details.value as unknown as IProductVariation[]).map(productVariation => productVariation._id);

    return {
      ...condition,
      details: {
        value: productVariationsIds,
        operator: condition.details.operator,
        quantity: condition.details.quantity
      }
    }
  }

  // if (condition.type === PromotionConditionType.TAGS) {
  //   const tagsIds = (condition.details.value as unknown as ITag[]).map(tag => tag._id);

  //   return {
  //     ...condition,
  //     details: {
  //       ...condition.details,
  //       value: tagsIds
  //     }
  //   }
  // }

  if (condition.type === PromotionConditionType.CUSTOMERS) {
    const customersIds = (condition.details.value as unknown as ICustomer[]).map(customer => customer._id);

    return {
      ...condition,
      details: {
        value: customersIds,
        operator: condition.details.operator,
      }
    }
  }

  if (condition.type === PromotionConditionType.PACKS) {
    const packsIds = (condition.details.value as unknown as IPack[]).map(pack => pack._id);

    return {
      ...condition,
      details: {
        value: packsIds,
        quantity: condition.details.quantity,
        operator: condition.details.operator,
      }
    }
  }

  if (condition.type === PromotionConditionType.ORDER_TOTAL) {
    return {
      ...condition,
      details: {
        operator: condition.details.operator,
        value: convertToCents(condition.details.value)
      }
    }
  }

  return condition;
}

const transformAction = (action: IPromotionAction): IOriginalPromotionAction => {
  if (action.type === PromotionActionType.ADD_PACK) {
    const pack = action.details.value as unknown as IPack;

    return {
      ...action,
      details: {
        value: pack._id
      }
    }
  }

  if (action.type === PromotionActionType.ADD_PRODUCT_VARIATION) {
    const productVariation = action.details.value as unknown as IProductVariation;
    return {
      ...action,
      details: {
        value: productVariation._id
      }
    }
  }

  if (action.type === PromotionActionType.ITEMS_DISCOUNT_FIXED) {
    const targets = action.details.targets = action.details.targets.map((target) => (target as unknown as { value: string }).value);

    return {
      ...action,
      details: {
        strategy: action.details.strategy,
        targets,
        value: convertToCents(action.details.value),
      }
    }
  }

  if (action.type === PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE) {
    const targets = action.details.targets = action.details.targets.map((target) => (target as unknown as { value: string }).value);

    return {
      ...action,
      details: {
        targets,
        value: action.details.value,
        strategy: action.details.strategy,
      }
    }
  }

  if (action.type === PromotionActionType.ORDER_DISCOUNT_FIXED) {
    return {
      ...action,
      details: {
        value: convertToCents(action.details.value),
      }
    }
  }

  return action;
}

export const transformStepFormValuesToPayloadPromotion = (values: PromotionFormValues) => {
  // Les clés privées de la copie sont retirées AVANT `createFormData`, qui sérialise tout objet ;
  // `copieDesImagesDe` part au serveur.
  const { isPromotionCode, availabilityType, ...rest } = sansLesClesPriveesDeCopie(values);

  const conditions = (values.conditions || []);
  const actions = (values.actions || []);

  const basePayload = {
    ...rest,
    maxUses: values.maxUses ?? '',
    startDate: values.startDate || '',
    endDate: values.endDate || '',
    shopsIds: (values.shops as unknown as IShop[]).map(shop => shop._id),
    conditions: conditions.length > 0 ? conditions.map(transformCondition) : [],
    actions: actions.length > 0 ? actions.map(transformAction) : [],
    availabilitySchedule: availabilityType === ScheduleAvailabilityTypeEnum.specificDays
      ? values.availabilitySchedule
      : {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      },
  };

  if (isPromotionCode) {
    return createFormData({
      ...basePayload,
      code: values.code,
      connectors: values.connectors
    }, );
  }

  const { code, connectors, ...payloadWithoutCodeAndConnectors } = basePayload;

  return createFormData(payloadWithoutCodeAndConnectors);
}

type WithName = {
  name: string;
}

export const getPromotionActionTargetAsIntelligibleLabel = (item: IOriginalPromotionCondition | IOriginalPromotionAction, index: number) => {
  if (item.type === PromotionConditionType.PACKS) {
    const temp = (item?.details?.value || []).map(pack => (pack as unknown as WithName).name).join(', ').substring(0, 33);
    const value = temp?.length >= 33 ? temp + '...' : temp;

    if (!item.details.quantity || !value) {
      return `(Condition ${index})`;
    }

    return `(Condition ${index}) Sur ${item.details.quantity} menu parmis (${value})`
  }

  if (item.type === PromotionConditionType.PRODUCT_VARIATIONS) {
    const temp = (item?.details?.value || []).map(product => (product as unknown as WithName).name).join(', ').substring(0, 33);
    const value = temp?.length >= 33 ? temp + '...' : temp;

    if (!item.details.quantity || !value) {
      return `(Condition ${index})`;
    }

    return `(Condition ${index}) Sur ${item.details.quantity} produits parmis (${value})`
  }

  if (item.type === PromotionActionType.ADD_PACK) {
    const temp = (item?.details?.value as unknown as WithName)?.name;
    const value = temp?.length >= 33 ? temp + '...' : temp;

    if (!value) {
      return `(Action ${index})`;
    }

    return `(Action ${index}) Sur le menu: ${value}`
  }

  if (item.type === PromotionActionType.ADD_PRODUCT_VARIATION) {
    const temp = (item?.details?.value as unknown as WithName)?.name;
    const value = temp?.length >= 33 ? temp + '...' : temp;

    if (!value) {
      return `(Action ${index})`;
    }

    return `(Action ${index}) Sur le produit: ${value}`
  }

  return ""
}

export const transformToUpdatableFormPromotion = (promotion?: Partial<IPromotion>): PromotionFormValues => {
  const packs = promotion?.packs ?? [];
  const productVariations = promotion?.products?.flatMap(product => product.productVariations) ?? [];

  const conditions = promotion?.conditions || [];
  const actions = promotion?.actions || [];

  const conditionEligibles = conditions.filter(condition => [PromotionConditionType.PACKS, PromotionConditionType.PRODUCT_VARIATIONS].includes(condition.type));
  const actionEligibles = actions.filter(action => [PromotionActionType.ADD_PACK, PromotionActionType.ADD_PRODUCT_VARIATION].includes(action.type));

  const base = [...actionEligibles, ...conditionEligibles]
    .map((item) => {
      const conditionIndex = conditions.findIndex(eligible => eligible.referenceId === item.referenceId)
      const actionIndex = actions.findIndex(eligible => eligible.referenceId === item.referenceId);

      const index = conditionIndex !== -1 ? conditionIndex : actionIndex;

      return { value: item.referenceId, label: getPromotionActionTargetAsIntelligibleLabel(item, index + 1) } as { value: string, label: string }
    });

  return {
    name: promotion?.name,
    image: null,
    priority: typeof promotion?.priority === 'string'
      ? parseInt(promotion?.priority)
      : promotion?.priority,
    scopes: promotion?.scopes || [PromotionScope.CATALOG],
    tags: promotion?.tags,
    isPromotionCode: promotion ? isPromotionCode(promotion) : false,
    connectors: promotion?.connectors,
    description: promotion?.description,
    code: promotion?.code,
    minPrice: promotion?.minPrice,
    uniqueByCustomer: Boolean(promotion?.uniqueByCustomer),
    maxUses: promotion?.maxUses,
    shops: promotion?.shops ?? [],
    repeatable: Boolean(promotion?.repeatable),
    orderModes: promotion?.orderModes,
    disabled: Boolean(promotion?.disabled),
    cumulative: Boolean(promotion?.cumulative),
    startDate: promotion?.startDate,
    endDate: promotion?.endDate,
    availabilityType: Object.values(promotion?.availabilitySchedule || {}).some((day) => day.length > 0)
      ? ScheduleAvailabilityTypeEnum.specificDays
      : ScheduleAvailabilityTypeEnum.always,
    availabilitySchedule: promotion?.availabilitySchedule,
    conditions: (promotion?.conditions ?? []).map(condition => {
      if (condition.type === PromotionConditionType.ORDER_TOTAL) {
        return {
          ...condition,
          details: {
            ...condition.details,
            value: convertToEuros(condition.details.value),
          }
        }
      }

      if (condition.type === PromotionConditionType.PRODUCT_VARIATIONS) {
        const value = condition.details.value
          .map(productVariationId => productVariations.find(productVariation => productVariation._id === productVariationId))
          .filter(productVariation => !!productVariation);

        return {
          ...condition,
          details: {
            ...condition.details,
            value
          }
        }
      }

      if (condition.type === PromotionConditionType.PACKS) {
        const value = condition.details.value
          .map(packId => packs.find(pack => pack._id === packId))
          .filter(pack => !!pack);

        return {
          ...condition,
          details: {
            ...condition.details,
            value
          }
        }
      }

      return condition;
    }),
    actions: (promotion?.actions ?? [])
      .map(action => {
        if (action.type === PromotionActionType.ADD_PRODUCT_VARIATION) {
          const value = productVariations.find(productVariation => productVariation._id === action.details.value)!;

          return {
            ...action,
            details: {
              ...action.details,
              value
            }
          }
        }

        if (action.type === PromotionActionType.ADD_PACK) {
          const value = packs.find(pack => pack._id === action.details.value)!;

          return {
            ...action,
            details: {
              ...action.details,
              value
            }
          }
        }

        if (action.type === PromotionActionType.ITEMS_DISCOUNT_FIXED) {
          return {
            ...action,
            details: {
              ...action.details,
              value: convertToEuros(action.details.value),
              targets: action.details.targets.map(target => {
                return base.find(item => item.value === target);
              })
            }
          }
        }

        if (action.type === PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE) {
          return {
            ...action,
            details: {
              ...action.details,
              targets: action.details.targets.map(target => {
                return base.find(item => item.value === target);
              })
            }
          }
        }

        if (action.type === PromotionActionType.ORDER_DISCOUNT_FIXED) {
          return {
            ...action,
            details: {
              ...action.details,
              value: convertToEuros(action.details.value),
            }
          }
        }

        return action;
      }),
  }
}
